import {DateTime} from 'luxon';

import {
  MESSAGES_KEYS,
  setValue as setMessageSeen,
} from '@explorer/util/local-messages';

export const TOUR_STAGES = {
  TIMELINE: 'timeline',
  SEARCH_INPUT: 'search-input',
  DRAW_TOOLS: 'draw-tools',
  SEARCH_SIDEBAR: 'search-sidebar',
  FILTERS: 'filters',
  COMPARE: 'compare',
  ORDER_BUTTON: 'order-button',
  ORDERS_SIDEBAR: 'orders-sidebar',
  FOLDERS_SIDEBAR: 'folders-sidebar',
  FOLDERS_INTRO: 'folders-intro',
  FOLDERS_DEMO: 'folders-demo',
  FOLDERS_DEMODATA: 'folders-demodata',
  OVERZOOM: 'imagery-overzoomed',
};

const today = DateTime.utc().endOf('day');

export const TOUR_SEARCH_DATES = [
  today.minus({days: 5}).toISO(),
  today.toISO(),
];

export const TOUR_GEOJSON = {
  type: 'FeatureCollection',
  features: [
    {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [-119.87251281738281, 36.86643755175846],
            [-119.81002807617188, 36.86643755175846],
            [-119.81002807617188, 36.92025402759534],
            [-119.87251281738281, 36.92025402759534],
            [-119.87251281738281, 36.86643755175846],
          ],
        ],
      },
    },
  ],
};

export const MAIN_STEPS = [
  {
    id: TOUR_STAGES.SEARCH_INPUT,
    disableBeacon: true,
    delay: 450,
    spotlightPadding: 4,
    placement: 'right',
    target: `[data-tour="${TOUR_STAGES.SEARCH_INPUT}"]`,
    content:
      'Start exploring by searching for locations of interest to you. You can also search using coordinates or scene ID.',
  },
  {
    id: TOUR_STAGES.DRAW_TOOLS,
    disableBeacon: true,
    spotlightPadding: 4,
    placement: 'left',
    target: `[data-tour="${TOUR_STAGES.DRAW_TOOLS}"]`,
    content:
      'These tools allow you to create or upload a geometry. A geometry defines your area of interest and it is required to search Planet’s daily scene catalog.',
  },
  {
    id: TOUR_STAGES.SEARCH_SIDEBAR,
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'right',
    target: `[data-tour="${TOUR_STAGES.SEARCH_SIDEBAR}"]`,
    delay: 350,
    spotlightClicks: true,
    content:
      'A list of scenes within your geometry will be displayed in the Daily Scenes drawer. Select or multi-select (shift + click) scenes to order.',
  },
  {
    id: TOUR_STAGES.TIMELINE,
    disableBeacon: true,
    delay: 500,
    spotlightPadding: 0,
    placement: 'top',
    target: `[data-tour="${TOUR_STAGES.TIMELINE}"]`,
    content:
      'This is the timeline: Select or use the arrow keys to move left or right, through time.',
  },
  {
    id: TOUR_STAGES.FILTERS,
    disableBeacon: true,
    spotlightPadding: 0,
    placement: 'right',
    target: `[data-tour="${TOUR_STAGES.FILTERS}"]`,
    content:
      'Use filtering to refine your search for specific scenes. You can also update your date range to display more specific dates.',
  },
  {
    id: TOUR_STAGES.COMPARE,
    disableBeacon: true,
    spotlightPadding: 4,
    placement: 'left',
    target: `[data-tour="${TOUR_STAGES.COMPARE}"]`,
    content:
      'The comparison tool helps to see scenes side-by-side and assess pixel differences between them.',
  },
  {
    id: TOUR_STAGES.ORDER_BUTTON,
    disableBeacon: true,
    spotlightPadding: 4,
    placement: 'top',
    delay: 350,
    target: `[data-tour="${TOUR_STAGES.ORDER_BUTTON}"]`,
    content:
      'Order your selected scenes by clicking the order button. You can order for download or for hosted data analysis.',
  },
  {
    id: TOUR_STAGES.ORDERS_SIDEBAR,
    disableBeacon: true,
    spotlightPadding: 4,
    placement: 'right',
    target: `[data-tour="${TOUR_STAGES.ORDERS_SIDEBAR}"]`,
    content:
      'You can view the status of your download and hosted analysis orders in “My Orders”.',
  },
];

/**
 * Exploratory work to have separate tours - not being used right now
 */
export const FOLDERS_STEPS = [
  {
    id: TOUR_STAGES.FOLDERS_INTRO,
    spotlightPadding: 0,
    placementBeacon: 'top',
    placement: 'right',
    disableOverlay: true,
    target: `[data-tour="${TOUR_STAGES.FOLDERS_INTRO}"]`,
    content:
      'If you haven’t ordered COGs or are not yet a customer, you can check out Planet’s demo data by selecting "Demo Data" above.',
  },
  {
    id: TOUR_STAGES.FOLDERS_DEMO,
    spotlightPadding: 0,
    disableBeacon: true,
    placement: 'right',
    disableOverlay: true,
    target: `[data-tour="${TOUR_STAGES.FOLDERS_DEMO}"]`,
    content: "Here you can see some demo folders, let's go into the first one.",
  },
  {
    id: TOUR_STAGES.FOLDERS_DEMODATA,
    spotlightPadding: 0,
    disableBeacon: true,
    placement: 'right',
    floaterProps: {
      hideArrow: true,
    },
    disableOverlay: true,
    target: `[data-tour="${TOUR_STAGES.FOLDERS_DEMODATA}"]`,
    content:
      'You can use our tools for analyzing this data, including visualization indices like NDVI and auto-enhancement of imagery',
  },
];

export const OVERZOOM_STEPS = [
  {
    id: TOUR_STAGES.OVERZOOM,
    spotlightPadding: 4,
    offset: -4,
    disableBeacon: true,
    placement: 'top',
    disableOverlay: true,
    target: `[data-tour="${TOUR_STAGES.OVERZOOM}"]`,
    title: 'Viewing overzoomed imagery',
    content: `This icon will indicate when you are overzoomed. 
    Overzoom is the result of zooming past the maximum zoom level for the selected imagery. 
    Overzoomed imagery is blurry and more difficult to interpret.`,
    onEnd: () => {
      setMessageSeen(MESSAGES_KEYS.OVERZOOM, true);
    },
    finishLabel: 'Got it',
  },
];

export const TOURS = {
  MAIN: 'main-tour',
  FOLDERS: 'folders',
  OVERZOOM: 'overzoom',
};

export const TOUR_STEPS = {
  [TOURS.MAIN]: MAIN_STEPS,
  [TOURS.FOLDERS]: FOLDERS_STEPS,
  [TOURS.OVERZOOM]: OVERZOOM_STEPS,
};
